import { authedFetch } from "./lib/api";

export interface Dream {
  id: string;
  title: string;
  prompt: string;
  media_ids: string[];
  status: "draft";
  created_at: string;
  created_by: string;
}

export interface GenerationProvider {
  id: string;
  label: string;
  model: string;
  available: boolean;
  paid: boolean;
  price_per_second: number;
  description: string;
}

export interface GenerationJob {
  id: string;
  dream_id: string;
  provider: string;
  model: string;
  aspect_ratio: "16:9" | "9:16";
  duration_seconds: 4 | 6 | 8;
  status: "queued" | "generating" | "uncertain" | "simulated" | "ready" | "failed";
  operation_id: string;
  poll_count: number;
  output_media_id: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  published_at: string | null;
  published_by: string | null;
}

async function responseError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { detail?: string };
    return body.detail ?? `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

export async function fetchDreams(): Promise<Dream[]> {
  const response = await authedFetch("/api/admin/dreams");
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<Dream[]>;
}

export async function createDream(input: {
  title: string;
  prompt: string;
  media_ids: string[];
}): Promise<Dream> {
  const response = await authedFetch("/api/admin/dreams", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<Dream>;
}

export async function deleteDream(dreamId: string): Promise<void> {
  const response = await authedFetch(`/api/admin/dreams/${encodeURIComponent(dreamId)}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error(await responseError(response));
}

export async function fetchGenerationProviders(): Promise<GenerationProvider[]> {
  const response = await authedFetch("/api/admin/generation-providers");
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationProvider[]>;
}

export async function fetchGenerationJobs(): Promise<GenerationJob[]> {
  const response = await authedFetch("/api/admin/generations");
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob[]>;
}

export async function startGeneration(
  dreamId: string,
  input: {
    provider: string;
    aspect_ratio: "16:9" | "9:16";
    duration_seconds: 4 | 6 | 8;
    confirmed_estimated_cost?: number;
  },
): Promise<GenerationJob> {
  const response = await authedFetch(
    `/api/admin/dreams/${encodeURIComponent(dreamId)}/generations`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob>;
}

export async function refreshGeneration(jobId: string): Promise<GenerationJob> {
  const response = await authedFetch(
    `/api/admin/generations/${encodeURIComponent(jobId)}/refresh`,
    { method: "POST" },
  );
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob>;
}

export async function abandonGeneration(jobId: string): Promise<GenerationJob> {
  const response = await authedFetch(
    `/api/admin/generations/${encodeURIComponent(jobId)}/abandon`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmed: true }),
    },
  );
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob>;
}

export async function publishGeneration(jobId: string): Promise<GenerationJob> {
  const response = await authedFetch(
    `/api/admin/generations/${encodeURIComponent(jobId)}/publish`,
    { method: "POST" },
  );
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob>;
}

export async function unpublishGeneration(jobId: string): Promise<GenerationJob> {
  const response = await authedFetch(
    `/api/admin/generations/${encodeURIComponent(jobId)}/publish`,
    { method: "DELETE" },
  );
  if (!response.ok) throw new Error(await responseError(response));
  return response.json() as Promise<GenerationJob>;
}
